import type { RealEstateCase } from "../../types";
import type { MarketAnalysis } from "../market/domain/types";
import type {
  MarketResponseBenchmark,
  MarketResponseStatus,
  MarketStatsSnapshot,
  MarketTimelineEvent
} from "./types";
import { calendarDayDifference, safelyDerivedMarketTimeline } from "./domain.ts";

export type MarketStatsDelta = {
  views?: number;
  detailViews?: number;
  calls?: number;
  emails?: number;
  saved?: number;
  viewings?: number;
};

export type MarketResponseEvaluation = {
  status: MarketResponseStatus;
  explanation: string;
  benchmarkSource?: string;
  benchmarkSampleSize?: number;
};

export type ClientMarketReport = {
  schemaVersion: "client-market-report-1";
  kind: "DAY_3" | "DAY_7" | "CURRENT";
  generatedAt: string;
  caseId: string;
  property: { label: string; address: string; area?: number };
  listingStartedAt?: string;
  currentAskingPrice?: number;
  stats?: MarketStatsSnapshot;
  previousStats?: MarketStatsSnapshot;
  delta?: MarketStatsDelta;
  topComparables: Array<{ id: string; label: string; askingPrice?: number; realizedPrice?: number; similarityScore: number }>;
  marketRange?: { low: number; high: number };
  pricePosition?: { amountAboveHigh: number; percentAboveHigh: number };
  marketResponse: MarketResponseEvaluation;
  timeline: MarketTimelineEvent[];
  nextRecommendedReview?: { date: string; time: string; purpose: string };
  methodologyNote: string;
  sourceNote: string;
};

export type SalesSummary = {
  schemaVersion: "sales-summary-1";
  caseId: string;
  daysOnMarket: number;
  initialListingPrice?: number;
  finalAskingPrice?: number;
  realizedPrice?: number;
  realizedVsInitialPercent?: number;
  priceChangeCount: number;
  totalCalls: number;
  totalViewings: number;
  reservationDay?: number;
  soldAt?: string;
};

const metricDelta = (current: MarketStatsSnapshot, previous?: MarketStatsSnapshot): MarketStatsDelta | undefined => {
  if (!previous) return undefined;
  const keys = ["views", "detailViews", "calls", "emails", "saved", "viewings"] as const;
  return Object.fromEntries(keys.flatMap((key) => current[key] !== undefined && previous[key] !== undefined ? [[key, current[key]! - previous[key]!]] : []));
};

export const evaluateMarketResponse = (snapshot: MarketStatsSnapshot | undefined, benchmark?: MarketResponseBenchmark | null): MarketResponseEvaluation => {
  if (!snapshot || !benchmark || benchmark.sampleSize <= 0) {
    return { status: "INSUFFICIENT_DATA", explanation: "Nedostatok historických dát." };
  }
  const ratios = [
    benchmark.averageCalls > 0 && snapshot.calls !== undefined ? snapshot.calls / benchmark.averageCalls : undefined,
    benchmark.averageViewings > 0 && snapshot.viewings !== undefined ? snapshot.viewings / benchmark.averageViewings : undefined,
    benchmark.averageDetailViewRatio && snapshot.views && snapshot.detailViews !== undefined
      ? (snapshot.detailViews / snapshot.views) / benchmark.averageDetailViewRatio
      : undefined
  ].filter((value): value is number => value !== undefined && Number.isFinite(value));
  if (ratios.length === 0) return { status: "INSUFFICIENT_DATA", explanation: "Nedostatok porovnateľných metrík.", benchmarkSource: benchmark.source, benchmarkSampleSize: benchmark.sampleSize };
  const average = ratios.reduce((sum, value) => sum + value, 0) / ratios.length;
  return {
    status: average < 0.75 ? "LOW_RESPONSE" : average > 1.25 ? "STRONG_RESPONSE" : "NORMAL_RESPONSE",
    explanation: "Vyhodnotenie používa iba dodaný historický benchmark FISPRO.",
    benchmarkSource: benchmark.source,
    benchmarkSampleSize: benchmark.sampleSize
  };
};

export const buildClientMarketReport = (
  caseItem: RealEstateCase,
  analyses: MarketAnalysis[],
  targetDay?: 3 | 7,
  benchmark?: MarketResponseBenchmark | null,
  generatedAt = new Date().toISOString()
): ClientMarketReport => {
  const timelineState = safelyDerivedMarketTimeline(caseItem);
  const snapshots = [...timelineState.statsSnapshots].sort((a, b) => a.dayFromListing - b.dayFromListing || Date.parse(a.capturedAt) - Date.parse(b.capturedAt));
  const targetSnapshots = targetDay ? snapshots.filter((snapshot) => snapshot.dayFromListing === targetDay) : snapshots;
  const stats = targetSnapshots[targetSnapshots.length - 1];
  const priorSnapshots = stats ? snapshots.filter((snapshot) => snapshot.dayFromListing < stats.dayFromListing) : [];
  const previousStats = priorSnapshots[priorSnapshots.length - 1];
  const latestAnalysis = analyses.filter((analysis) => analysis.caseId === caseItem.id && analysis.status === "finalized" && analysis.resultSnapshot).sort((a, b) => Date.parse(b.finalizedAt ?? b.updatedAt) - Date.parse(a.finalizedAt ?? a.updatedAt))[0];
  const result = latestAnalysis?.resultSnapshot;
  const amountAboveHigh = result && caseItem.askingPrice > result.marketHigh ? caseItem.askingPrice - result.marketHigh : 0;
  return {
    schemaVersion: "client-market-report-1",
    kind: targetDay === 3 ? "DAY_3" : targetDay === 7 ? "DAY_7" : "CURRENT",
    generatedAt,
    caseId: caseItem.id,
    property: { label: [caseItem.apartmentRooms, caseItem.propertyType].filter(Boolean).join(" "), address: caseItem.propertyAddress, area: caseItem.area },
    listingStartedAt: caseItem.listingStartedAt,
    currentAskingPrice: caseItem.askingPrice > 0 ? caseItem.askingPrice : undefined,
    stats,
    previousStats,
    delta: stats ? metricDelta(stats, previousStats) : undefined,
    topComparables: (latestAnalysis?.comparables ?? []).filter((comparable) => comparable.decision !== "excluded").sort((a, b) => b.similarityScore - a.similarityScore).slice(0, 3).map((comparable) => ({ id: comparable.id, label: [comparable.street, comparable.city].filter(Boolean).join(", ") || comparable.propertyType, askingPrice: comparable.askingPrice, realizedPrice: comparable.realizedPrice, similarityScore: comparable.similarityScore })),
    marketRange: result ? { low: result.marketLow, high: result.marketHigh } : undefined,
    pricePosition: result ? { amountAboveHigh, percentAboveHigh: result.marketHigh > 0 ? Math.round((amountAboveHigh / result.marketHigh) * 1000) / 10 : 0 } : undefined,
    marketResponse: evaluateMarketResponse(stats, benchmark),
    timeline: [...timelineState.events].sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt)),
    nextRecommendedReview: caseItem.followUp,
    methodologyNote: result ? `Cenové pásmo pochádza z CMA metodiky ${result.methodologyVersion}.` : "Cenové pásmo nie je dostupné bez dokončenej CMA analýzy.",
    sourceNote: "Portálové, CRM, market a manuálne údaje sú označené samostatným zdrojom. Ponuková cena nie je realizovaná cena."
  };
};

export const buildSalesSummary = (caseItem: RealEstateCase): SalesSummary => {
  const timelineState = safelyDerivedMarketTimeline(caseItem);
  const events = timelineState.events;
  const latestStats = [...timelineState.statsSnapshots].sort((a, b) => Date.parse(b.capturedAt) - Date.parse(a.capturedAt))[0];
  const reservation = events.find((event) => event.type === "RESERVED");
  const soldAt = caseItem.soldAt ?? events.find((event) => event.type === "SOLD")?.occurredAt;
  return {
    schemaVersion: "sales-summary-1",
    caseId: caseItem.id,
    daysOnMarket: caseItem.listingStartedAt && soldAt ? calendarDayDifference(caseItem.listingStartedAt, soldAt) : 0,
    initialListingPrice: caseItem.initialListingPrice,
    finalAskingPrice: caseItem.askingPrice > 0 ? caseItem.askingPrice : undefined,
    realizedPrice: caseItem.realizedPrice,
    realizedVsInitialPercent: caseItem.realizedPrice && caseItem.initialListingPrice ? Math.round((caseItem.realizedPrice / caseItem.initialListingPrice) * 1000) / 10 : undefined,
    priceChangeCount: events.filter((event) => event.type === "PRICE_CHANGED").length,
    totalCalls: latestStats?.calls ?? events.filter((event) => event.type === "FIRST_CALL").length,
    totalViewings: latestStats?.viewings ?? events.filter((event) => event.type === "FIRST_VIEWING" || event.type === "VIEWING").length,
    reservationDay: reservation && caseItem.listingStartedAt ? calendarDayDifference(caseItem.listingStartedAt, reservation.occurredAt) : undefined,
    soldAt
  };
};
